/**
 * Business Intelligence Component - Main Implementation
 * 
 * Provides business intelligence functionality with Google Places integration
 */

import React, { useState, useEffect } from 'react';
import { Project } from '../../../../services/projects';
import { useBusinessIntelligenceStore } from '../../../../stores/businessIntelligenceStore';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { ErrorDisplay } from './components/common/ErrorDisplay';

interface BusinessIntelligenceProps {
  project: Project;
}

const BusinessIntelligence: React.FC<BusinessIntelligenceProps> = ({ project }) => {
  // Store state
  const {
    businessIntelligence,
    isLoading,
    error,
    formData,
    isFormDirty,
    isSaving,
    saveError,
    isDiscovering,
    discoveryResults,
    discoveryError,
    loadBusinessIntelligence,
    saveBusinessIntelligence,
    updateFormData,
    discoverCompetitors,
    fetchFromGoogle,
    addCompetitorFromGoogle
  } = useBusinessIntelligenceStore();

  // Local state
  const [activeSection, setActiveSection] = useState<'overview' | 'competition' | 'attractions' | 'pricing' | 'operations' | 'marketing'>('overview');
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [selectedCompetitors, setSelectedCompetitors] = useState<Set<string>>(new Set());
  const [showGoogleSync, setShowGoogleSync] = useState(false);
  const [googleSyncData, setGoogleSyncData] = useState<any>(null);

  // Initialize form data with project info
  useEffect(() => {
    updateFormData({
      companyProfile: {
        locationName: project.name || '',
        address: typeof project.location === 'string' ? project.location : project.location?.address || '',
        isExistingLocation: false,
        isFranchise: false
      }
    });
  }, [project, updateFormData]);

  // Form handlers
  const handleFormChange = (section: string, data: any) => {
    updateFormData({ [section]: data });
  };

  const handleSave = async () => {
    if (!project.id) return;
    await saveBusinessIntelligence(project.id);
  };

  const handleFetchFromGoogle = async (businessName: string, address?: string) => {
    if (!project.id) return;
    
    try {
      const result = await fetchFromGoogle(project.id, businessName, address);
      setGoogleSyncData(result);
      setShowGoogleSync(true);
    } catch (error) {
      console.error('Failed to fetch from Google:', error);
    }
  };

  const handleDiscoverCompetitors = async (location: string, radius: number, keyword: string) => {
    if (!project.id) return;
    await discoverCompetitors(project.id, location, radius, keyword);
  };

  const handleAddCompetitor = async (placeId: string, competitorName?: string) => {
    if (!project.id) return;
    try {
      await addCompetitorFromGoogle(project.id, placeId, competitorName);
      setSelectedCompetitors(prev => {
        const newSet = new Set(prev);
        newSet.delete(placeId);
        return newSet;
      });
    } catch (error) {
      console.error('Failed to add competitor:', error);
    }
  };

  const handleApplyGoogleData = (selectedFields: string[]) => {
    if (!googleSyncData) return;
    
    const updates: any = {};
    
    if (selectedFields.includes('name')) {
      updates.companyProfile = {
        ...formData.companyProfile,
        locationName: googleSyncData.name
      };
    }
    
    if (selectedFields.includes('address')) {
      updates.companyProfile = {
        ...formData.companyProfile,
        address: googleSyncData.formatted_address
      };
    }
    
    updateFormData(updates);
    setShowGoogleSync(false);
    setGoogleSyncData(null);
  };

  // Render loading state for initial load
  if (isLoading && !businessIntelligence) {
    return (
      <div className="flex items-center justify-center p-12">
        <LoadingSpinner size="lg" message="Loading business intelligence data..." />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="border-b border-gray-200 pb-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Business Intelligence</h2>
            <p className="mt-1 text-gray-600">
              Manage your business profile and competitive intelligence with Google Places integration
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setAiAssistantOpen(!aiAssistantOpen)}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                isDiscovering 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              disabled={isDiscovering}
            >
              {isDiscovering ? (
                <>
                  <LoadingSpinner size="sm" className="inline-block mr-2" />
                  AI Working...
                </>
              ) : (
                <>
                  <svg className="mr-2 -ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  AI Assistant
                </>
              )}
            </button>
            <button
              onClick={handleSave}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isSaving || !isFormDirty
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              disabled={isSaving || !isFormDirty}
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size="sm" className="inline-block mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <svg className="mr-2 -ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      <ErrorDisplay 
        error={error || saveError || discoveryError} 
        className="mb-6"
      />

      {/* AI Assistant Panel */}
      {aiAssistantOpen && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-100">
                <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Assistant</h3>
              <p className="text-gray-600 mb-4">
                Use AI to automatically discover competitors and enhance your business profile with market data.
              </p>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => handleFetchFromGoogle(formData.companyProfile?.locationName || '', formData.companyProfile?.address)}
                  disabled={isDiscovering}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 ${
                    isDiscovering 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  <svg className="mr-2 -ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Fetch from Google Places
                </button>
                <button
                  onClick={() => setActiveSection('competition')}
                  disabled={isDiscovering}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${
                    isDiscovering 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  <svg className="mr-2 -ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Discover Competitors
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Data Sync Modal */}
      {showGoogleSync && googleSyncData && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Google Places Data Sync</h3>
              <p className="mt-1 text-sm text-gray-600">Select the data you want to import from Google Places</p>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <input type="checkbox" id="import-name" className="mr-3" />
                  <label htmlFor="import-name" className="font-medium">Business Name: {googleSyncData.name}</label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="import-address" className="mr-3" />
                  <label htmlFor="import-address" className="font-medium">Address: {googleSyncData.formatted_address}</label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="import-website" className="mr-3" />
                  <label htmlFor="import-website" className="font-medium">Website: {googleSyncData.website || 'Not available'}</label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="import-rating" className="mr-3" />
                  <label htmlFor="import-rating" className="font-medium">Rating: {googleSyncData.rating || 'Not available'}</label>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowGoogleSync(false);
                  setGoogleSyncData(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApplyGoogleData(['name', 'address', 'website', 'rating'])}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Apply Selected Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {[
            { key: 'overview', label: 'Business Overview', icon: '🏢' },
            { key: 'competition', label: 'Competition', icon: '🎯' },
            { key: 'attractions', label: 'Attractions', icon: '🎮' },
            { key: 'pricing', label: 'Pricing & Packages', icon: '💰' },
            { key: 'operations', label: 'Operations', icon: '⚙️' },
            { key: 'marketing', label: 'Marketing', icon: '📢' }
          ].map(section => (
            <button
              key={section.key}
              onClick={() => setActiveSection(section.key as any)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSection === section.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{section.icon}</span>
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Section Content */}
      <div className="min-h-96">
        {activeSection === 'overview' && (
          <BusinessOverviewSection 
            data={formData.companyProfile || {}} 
            onChange={(data) => handleFormChange('companyProfile', data)}
            onFetchFromGoogle={handleFetchFromGoogle}
          />
        )}
        
        {activeSection === 'competition' && (
          <CompetitionSection
            projectId={project.id}
            competitors={businessIntelligence?.competitor_profiles || []}
            discoveryResults={discoveryResults}
            isDiscovering={isDiscovering}
            selectedCompetitors={selectedCompetitors}
            onDiscoverCompetitors={handleDiscoverCompetitors}
            onAddCompetitor={handleAddCompetitor}
            onSelectCompetitor={(placeId: string, selected: boolean) => {
              const newSelection = new Set(selectedCompetitors);
              if (selected) {
                newSelection.add(placeId);
              } else {
                newSelection.delete(placeId);
              }
              setSelectedCompetitors(newSelection);
            }}
          />
        )}
        
        {activeSection === 'attractions' && (
          <AttractionsSection 
            data={formData.attractions || []} 
            onChange={(data) => handleFormChange('attractions', data)} 
          />
        )}
        
        {activeSection === 'pricing' && (
          <PricingSection 
            membershipPlans={formData.membershipPlans || []}
            birthdayPackages={formData.birthdayPackages || []}
            onMembershipChange={(data) => handleFormChange('membershipPlans', data)}
            onBirthdayChange={(data) => handleFormChange('birthdayPackages', data)}
          />
        )}
        
        {activeSection === 'operations' && (
          <OperationsSection 
            ratings={formData.operationalRatings || {}}
            foodAndBeverage={formData.foodAndBeverage || {}}
            onlinePresence={formData.onlinePresence || {}}
            onRatingsChange={(data) => handleFormChange('operationalRatings', data)}
            onFoodChange={(data) => handleFormChange('foodAndBeverage', data)}
            onOnlineChange={(data) => handleFormChange('onlinePresence', data)}
          />
        )}
        
        {activeSection === 'marketing' && (
          <MarketingSection 
            data={formData.marketingChannels || []} 
            onChange={(data) => handleFormChange('marketingChannels', data)} 
          />
        )}
      </div>
    </div>
  );
};

// Section components with proper TypeScript types
const BusinessOverviewSection: React.FC<{
  data: any;
  onChange: (data: any) => void;
  onFetchFromGoogle: (businessName: string, address?: string) => void;
}> = ({ data, onChange, onFetchFromGoogle }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Overview</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
        <input
          type="text"
          value={data.locationName || ''}
          onChange={(e) => onChange({ ...data, locationName: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter business name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
        <input
          type="text"
          value={data.address || ''}
          onChange={(e) => onChange({ ...data, address: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter address"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
        <input
          type="url"
          value={data.website || ''}
          onChange={(e) => onChange({ ...data, website: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="https://example.com"
        />
      </div>
      <div className="flex items-end">
        <button
          onClick={() => onFetchFromGoogle(data.locationName, data.address)}
          className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
        >
          Fetch from Google
        </button>
      </div>
    </div>
  </div>
);

const CompetitionSection: React.FC<{
  projectId: string;
  competitors: any[];
  discoveryResults: any[];
  isDiscovering: boolean;
  selectedCompetitors: Set<string>;
  onDiscoverCompetitors: (location: string, radius: number, keyword: string) => void;
  onAddCompetitor: (placeId: string, competitorName?: string) => void;
  onSelectCompetitor: (placeId: string, selected: boolean) => void;
}> = ({ projectId, competitors, discoveryResults, isDiscovering, onDiscoverCompetitors }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Competitor Discovery</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <input
          type="text"
          placeholder="Location (e.g., New York, NY)"
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="5">5 miles</option>
          <option value="10">10 miles</option>
          <option value="25">25 miles</option>
        </select>
        <input
          type="text"
          placeholder="Keyword (e.g., family entertainment)"
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <button
        onClick={() => onDiscoverCompetitors('New York, NY', 10, 'family entertainment')}
        disabled={isDiscovering}
        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-gray-400"
      >
        {isDiscovering ? 'Discovering...' : 'Discover Competitors'}
      </button>
    </div>
    
    {discoveryResults && discoveryResults.length > 0 && (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Discovered Competitors</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {discoveryResults.map((competitor: any) => (
            <div key={competitor.place_id} className="border border-gray-200 rounded-lg p-4">
              <h5 className="font-semibold text-gray-900">{competitor.name}</h5>
              <p className="text-sm text-gray-600 mb-2">{competitor.vicinity}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-yellow-600">★ {competitor.rating || 'N/A'}</span>
                <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                  Add
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
    
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h4 className="text-md font-semibold text-gray-900 mb-4">Current Competitors</h4>
      {competitors.length === 0 ? (
        <p className="text-gray-500">No competitors added yet. Use the discovery tool above to find competitors.</p>
      ) : (
        <div className="space-y-3">
          {competitors.map((competitor: any, index: number) => (
            <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
              <div>
                <h5 className="font-medium text-gray-900">{competitor.name}</h5>
                <p className="text-sm text-gray-600">{competitor.address}</p>
              </div>
              <button className="text-red-600 hover:text-red-800">Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const AttractionsSection: React.FC<{
  data: any[];
  onChange: (data: any[]) => void;
}> = ({ data, onChange }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Attractions & Activities</h3>
    <p className="text-gray-600">Configure your attractions and activities here.</p>
  </div>
);

const PricingSection: React.FC<{
  membershipPlans: any[];
  birthdayPackages: any[];
  onMembershipChange: (data: any[]) => void;
  onBirthdayChange: (data: any[]) => void;
}> = ({ membershipPlans, birthdayPackages }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Packages</h3>
    <p className="text-gray-600">Manage your pricing and package information here.</p>
  </div>
);

const OperationsSection: React.FC<{
  ratings: any;
  foodAndBeverage: any;
  onlinePresence: any;
  onRatingsChange: (data: any) => void;
  onFoodChange: (data: any) => void;
  onOnlineChange: (data: any) => void;
}> = ({ ratings, foodAndBeverage, onlinePresence }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Operations</h3>
    <p className="text-gray-600">Configure operational settings and ratings here.</p>
  </div>
);

const MarketingSection: React.FC<{
  data: any[];
  onChange: (data: any[]) => void;
}> = ({ data, onChange }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Marketing Channels</h3>
    <p className="text-gray-600">Manage your marketing channels and campaigns here.</p>
  </div>
);

export default BusinessIntelligence;
